// Survival: headline after headline from the whole archive, each staking a tenth of what is left,
// until the money runs out or the player walks away with it. The best walk-away is kept.

import { useMemo, useState } from "react";
import { Link } from "../components/Link";
import { Globe } from "../globe/Globe";
import type { GlobePoint } from "../globe/types";
import { dollars } from "../../shared/game/money.ts";
import { finishRun, markOf, record, START_CENTS, useBestRun, type Play as PlayRecord } from "./record";
import type { Round } from "./rounds";
import { archiveOf, Stage, type Leverage, type Outcome } from "./Stage";
import "./game.css";

/** A tenth of the bankroll a headline, in whole dollars, never under $100. */
const STAKE_SHARE = 10n;
const FLOOR_CENTS = 10_000n;

const stakeOf = (bankroll: bigint) => {
  const tenth = (bankroll / STAKE_SHARE / 100n) * 100n;
  return tenth < FLOOR_CENTS ? FLOOR_CENTS : tenth;
};

function shuffled(rounds: Round[]): Round[] {
  const out = [...rounds];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j] as Round, out[i] as Round];
  }
  return out;
}

type Run = { order: Round[]; index: number; bankroll: bigint; peak: bigint; plays: PlayRecord[]; over: "broke" | "walked" | null; best: boolean };

const fresh = (rounds: Round[]): Run => ({ order: shuffled(rounds), index: 0, bankroll: START_CENTS, peak: START_CENTS, plays: [], over: null, best: false });

export function Survival({ rounds }: { rounds: Round[] }) {
  const [run, setRun] = useState<Run>(() => fresh(rounds));
  const [leverage, setLeverage] = useState<Leverage>(50);
  const archive = useMemo(() => archiveOf(rounds), [rounds]);
  const best = useBestRun();

  const round = run.order[run.index % run.order.length] as Round;
  const stake = stakeOf(run.bankroll);

  const apply = (outcome: Outcome, walking: boolean) => {
    const play: PlayRecord = {
      round: round.id,
      deck: "survival",
      day: null,
      side: outcome.side,
      leverage: outcome.leverage,
      closedAt: outcome.closedAt,
      pnl: outcome.pnl.toString(),
      busted: outcome.busted,
      category: round.category,
      at: Date.now(),
    };
    record(play);
    const bankroll = run.bankroll + outcome.pnl;
    const next: Run = {
      ...run,
      bankroll,
      peak: bankroll > run.peak ? bankroll : run.peak,
      plays: [...run.plays, play],
      index: run.index + 1,
      // a lap through the archive reshuffles it
      order: (run.index + 1) % run.order.length === 0 ? shuffled(rounds) : run.order,
    };
    const over = bankroll < FLOOR_CENTS ? "broke" : walking ? "walked" : null;
    if (over) {
      const beat = finishRun({ bankroll: (over === "broke" ? 0n : bankroll).toString(), rounds: next.plays.length, at: Date.now() });
      setRun({ ...next, over, best: beat && over === "walked" });
    } else setRun(next);
    window.scrollTo({ top: 0 });
  };

  const points: GlobePoint[] = useMemo(
    () => (round.place ? [{ id: round.id, lat: round.place.lat, lon: round.place.lon, weight: 0.7, tone: "warn", label: round.place.name }] : []),
    [round],
  );

  if (run.over) {
    return (
      <main className="gm gm-summary">
        <div className="gm-summary-kicker num">{run.over === "broke" ? `Broke after ${run.plays.length} headlines` : `Walked away after ${run.plays.length} headlines`}</div>
        <div className={`gm-summary-bank num ${run.over === "walked" && run.bankroll >= START_CENTS ? "up" : "down"}`}>{dollars(run.over === "broke" ? 0n : run.bankroll)}</div>
        <div className="gm-summary-from num">
          peak {dollars(run.peak)}
          {run.best ? " · your best run" : best ? ` · best ${dollars(BigInt(best.bankroll))}` : ""}
        </div>
        <div className="gm-summary-marks gm-summary-marks-long" aria-label="Your rounds">
          {run.plays.map((play, i) => (
            <span key={`${play.round}-${i}`}>{markOf(play)}</span>
          ))}
        </div>
        <div className="gm-summary-actions">
          <button type="button" className="gm-cta" onClick={() => setRun(fresh(rounds))}>
            Again
          </button>
          <Link href="/" className="gm-secondary">
            Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="gm">
      <header className="gm-top">
        <span className="gm-count num">Survival · {run.plays.length + 1}</span>
        <span className="gm-stake-chip num">stake {dollars(stake)}</span>
        <span className={`gm-bank num ${run.bankroll < START_CENTS ? "down" : ""}`}>{dollars(run.bankroll)}</span>
      </header>
      <div className="gm-place">
        <Globe points={points} selected={round.id} quake={0.55} height="100%" />
        {round.place && <span className="gm-place-name">{round.place.name}</span>}
      </div>
      <Stage
        key={`${round.id}-${run.index}`}
        round={round}
        last={false}
        leverage={leverage}
        margin={stake}
        archive={archive}
        onLeverage={setLeverage}
        onDone={(outcome) => apply(outcome, false)}
        aside={(outcome) =>
          run.bankroll + outcome.pnl >= FLOOR_CENTS ? (
            <button type="button" className="gm-walk" onClick={() => apply(outcome, true)}>
              Walk away with {dollars(run.bankroll + outcome.pnl)}
            </button>
          ) : null
        }
      />
    </main>
  );
}
